import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { copy } from "shared/assets";
import { DeleteModal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { API_ENDPOINTS } from "shared/constants";
import { IAPIState, IStatus } from "shared/types";
import { getIdFromPath } from "shared/utils";
import { useVenuePageContext } from "Venues/contexts";
import { clearActionPagination, dataMetaSelector, deleteEquipment } from "Venues/state";
import { IEquipment, VENUE_ACTIONS } from "Venues/types";
import { getEquipmentKey, getStageEquipmentKey, useClearActionMetaOnUnMount } from "Venues/utils";

interface DeleteEquipmentModalProps {
  equipment?: IEquipment;
  handleClose: () => void;
  onSuccess?: () => void;
}

export const DeleteEquipmentModal: React.FC<DeleteEquipmentModalProps> = ({
  equipment,
  handleClose,
  onSuccess,
}) => {
  const { venueId } = useVenuePageContext();
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(dataMetaSelector(VENUE_ACTIONS.deleteEquipment));
  useClearActionMetaOnUnMount(VENUE_ACTIONS.deleteEquipment);

  const name = useMemo(() => {
    if (equipment) {
      const { single, multiple } = copy.deleteEquipment.detail;
      const stageCount = equipment.stages.length;
      const detailMessage =
        stageCount === 1 ? single : multiple.replace("%s", stageCount.toString());

      return `${equipment.brand} ${equipment.model} ${detailMessage}`;
    }
    return "";
  }, [equipment]);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: copy.deleteEquipment.success,
        })
      );
      handleClose();

      // clear pagination for chosen stages/category to force fetch the overview tables
      equipment?.stages.forEach(({ id }) => {
        dispatch(
          clearActionPagination(
            getStageEquipmentKey(getIdFromPath(id, API_ENDPOINTS.STAGES), equipment.category)
          )
        );
      });

      // clear pagination for venue equipment overview table
      dispatch(clearActionPagination(getEquipmentKey(venueId)));

      // refetch equipment after pagination is cleared
      onSuccess && onSuccess();
    }
  }, [apiMeta, dispatch, handleClose, onSuccess, venueId, equipment?.stages, equipment?.category]);

  const handleDelete = () => {
    if (equipment?.id) {
      dispatch(deleteEquipment(equipment.id));
    }
  };

  return (
    <DeleteModal
      type={copy.equipmentProperties.equipment.title}
      name={name}
      apiMeta={apiMeta}
      handleClose={handleClose}
      handleDelete={handleDelete}
    />
  );
};
